import { createContext, useContext, useState, useCallback } from 'react';

const RouteContext = createContext(null);

export const useRoute = () => {
  const context = useContext(RouteContext);
  if (!context) {
    throw new Error('useRoute must be used within a RouteProvider');
  }
  return context;
};

export const RouteProvider = ({ children }) => {
  const [startAddress, setStartAddress] = useState({ address: '', placeId: '', location: null });
  const [subjectProperty, setSubjectProperty] = useState({ address: '', placeId: '', location: null });
  const [comparables, setComparables] = useState([
    { id: 1, address: '', placeId: '', location: null }
  ]);
  const [finishAddress, setFinishAddress] = useState({ address: '', placeId: '', location: null });
  
  const [routeResult, setRouteResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [optimizedOrder, setOptimizedOrder] = useState([]);

  const addComparable = useCallback(() => {
    if (comparables.length < 4) {
      const newId = Math.max(...comparables.map(c => c.id), 0) + 1;
      setComparables(prev => [...prev, { id: newId, address: '', placeId: '', location: null }]);
    }
  }, [comparables.length]);

  const removeComparable = useCallback((id) => {
    if (comparables.length > 1) {
      setComparables(prev => prev.filter(c => c.id !== id));
    }
  }, [comparables.length]);

  const updateComparable = useCallback((id, data) => {
    setComparables(prev => prev.map(c => c.id === id ? { ...c, ...data } : c));
  }, []);

  const clearRoute = useCallback(() => {
    setStartAddress({ address: '', placeId: '', location: null });
    setSubjectProperty({ address: '', placeId: '', location: null });
    setComparables([{ id: 1, address: '', placeId: '', location: null }]);
    setFinishAddress({ address: '', placeId: '', location: null });
    setRouteResult(null);
    setOptimizedOrder([]);
  }, []);

  const hasValidAddresses = useCallback(() => {
    const validStart = startAddress.placeId || startAddress.location;
    const validFinish = finishAddress.placeId || finishAddress.location;
    const validSubject = subjectProperty.placeId || subjectProperty.location;
    return validStart && validFinish && validSubject;
  }, [startAddress, finishAddress, subjectProperty]);

  const getAllStops = useCallback(() => {
    const stops = [];
    
    if (startAddress.location) {
      stops.push({ type: 'start', ...startAddress });
    }
    
    if (subjectProperty.location) {
      stops.push({ type: 'subject', ...subjectProperty });
    }
    
    comparables.forEach((comp, index) => {
      if (comp.location) {
        stops.push({ type: 'comparable', index: index + 1, ...comp });
      }
    });
    
    if (finishAddress.location) {
      stops.push({ type: 'finish', ...finishAddress });
    }
    
    return stops;
  }, [startAddress, subjectProperty, comparables, finishAddress]);

  const value = {
    startAddress,
    setStartAddress,
    subjectProperty,
    setSubjectProperty,
    comparables,
    setComparables,
    addComparable,
    removeComparable,
    updateComparable,
    finishAddress,
    setFinishAddress,
    routeResult,
    setRouteResult,
    isCalculating,
    setIsCalculating,
    optimizedOrder,
    setOptimizedOrder,
    clearRoute,
    hasValidAddresses,
    getAllStops,
  };

  return (
    <RouteContext.Provider value={value}>
      {children}
    </RouteContext.Provider>
  );
};
